import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Timer, Buildings, Funnel, Plus, X, Lightning, ArrowsClockwise, FloppyDisk,
} from "@phosphor-icons/react";

const DAYS = [
  { val: 0, label: "Mon" },
  { val: 1, label: "Tue" },
  { val: 2, label: "Wed" },
  { val: 3, label: "Thu" },
  { val: 4, label: "Fri" },
  { val: 5, label: "Sat" },
  { val: 6, label: "Sun" },
];

const UNIT_OPTIONS = [
  { value: "minutes", label: "Minutes" },
  { value: "hours", label: "Hours" },
  { value: "days", label: "Days" },
];

// ---- unit conversion helpers --------------------------------------------------
// First-response canonical storage = MINUTES (business minutes when business
// hours mode is on).
// Time-to-close canonical storage  = HOURS (business hours when business hours
// mode is on).
// When business hours mode is on, the user's mental model of "Days" is
// **business days**, not 24-hour calendar days. So we convert days using the
// configured business-hours-per-day (e.g. 08:00–16:30 = 8.5 h/day). When
// business hours mode is off, days are flat 24-hour calendar days.
const businessHoursPerDay = (bhOnly, start, end) => {
  if (!bhOnly) return 24;
  try {
    const [sh, sm] = (start || "08:00").split(":").map(Number);
    const [eh, em] = (end || "16:30").split(":").map(Number);
    const mins = (eh * 60 + em) - (sh * 60 + sm);
    return mins > 0 ? mins / 60 : 8.5;
  } catch {
    return 8.5;
  }
};

const minutesToUnit = (minutes, unit, hoursPerDay = 24) => {
  if (minutes == null || minutes === "" || isNaN(minutes)) return "";
  const m = Number(minutes);
  if (unit === "hours") return roundNice(m / 60);
  if (unit === "days") return roundNice(m / (60 * hoursPerDay));
  return roundNice(m);
};
const unitToMinutes = (value, unit, hoursPerDay = 24) => {
  if (value === "" || value == null || isNaN(value)) return null;
  const v = Number(value);
  if (unit === "hours") return Math.round(v * 60);
  if (unit === "days") return Math.round(v * 60 * hoursPerDay);
  return Math.round(v);
};
const hoursToUnit = (hours, unit, hoursPerDay = 24) => {
  if (hours == null || hours === "" || isNaN(hours)) return "";
  const h = Number(hours);
  if (unit === "minutes") return roundNice(h * 60);
  if (unit === "days") return roundNice(h / hoursPerDay);
  return roundNice(h);
};
const unitToHours = (value, unit, hoursPerDay = 24) => {
  if (value === "" || value == null || isNaN(value)) return null;
  const v = Number(value);
  if (unit === "minutes") return Math.round((v / 60) * 100) / 100;
  if (unit === "days") return Math.round(v * hoursPerDay);
  return Math.round(v);
};
const roundNice = (n) => {
  // keep up to 2 decimals, but drop trailing zeros
  const r = Math.round(n * 100) / 100;
  return r;
};

// ---- inline number + unit picker ---------------------------------------------
function NumberWithUnit({ value, unit, onChange, onUnitChange, testid }) {
  return (
    <div className="mt-2 flex items-stretch gap-2">
      <Input
        data-testid={`${testid}-input`}
        type="number"
        min="0"
        step="any"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 rounded-sm flex-1"
      />
      <Select value={unit} onValueChange={onUnitChange}>
        <SelectTrigger
          data-testid={`${testid}-unit`}
          className="h-10 w-32 rounded-sm shrink-0"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {UNIT_OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function SlaTab({ settings, onSaved }) {
  // canonical minute / hour values are stored in form.* (unchanged keys).
  // display state for the numeric inputs is unit-aware:
  const [form, setForm] = useState(() => ({
    sla_first_response_minutes: settings.sla_first_response_minutes || 120,
    sla_first_response_unit: settings.sla_first_response_unit || "hours",
    sla_time_to_close_hours: settings.sla_time_to_close_hours || 168,
    sla_time_to_close_unit: settings.sla_time_to_close_unit || "days",
    sla_business_hours_only: !!settings.sla_business_hours_only,
    sla_business_start: settings.sla_business_start || "08:00",
    sla_business_end: settings.sla_business_end || "16:30",
    sla_business_days: settings.sla_business_days || [0, 1, 2, 3, 4],
    sla_breach_notify_enabled: !!settings.sla_breach_notify_enabled,
    sla_department_overrides: settings.sla_department_overrides || {},
    sla_source_overrides: settings.sla_source_overrides || {},
  }));
  const [firstDisplay, setFirstDisplay] = useState(() => {
    const hpd = businessHoursPerDay(
      settings.sla_business_hours_only !== false,
      settings.sla_business_start || "08:00",
      settings.sla_business_end || "16:30",
    );
    return String(minutesToUnit(settings.sla_first_response_minutes || 120, settings.sla_first_response_unit || "hours", hpd));
  });
  const [closeDisplay, setCloseDisplay] = useState(() => {
    const hpd = businessHoursPerDay(
      settings.sla_business_hours_only !== false,
      settings.sla_business_start || "08:00",
      settings.sla_business_end || "16:30",
    );
    return String(hoursToUnit(settings.sla_time_to_close_hours || 168, settings.sla_time_to_close_unit || "days", hpd));
  });

  const [departments, setDepartments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [recomputing, setRecomputing] = useState(false);

  useEffect(() => {
    api.get("/users/departments").then(({ data }) => setDepartments(data.departments || [])).catch(() => {});
  }, []);

  // Current business-hours-per-day, derived from the live form values. All
  // unit conversions key off this so "1 day" always means "1 business day"
  // when business hours mode is on.
  const hpd = businessHoursPerDay(form.sla_business_hours_only, form.sla_business_start, form.sla_business_end);

  const onFirstValueChange = (v) => {
    setFirstDisplay(v);
    const mins = unitToMinutes(v, form.sla_first_response_unit, hpd);
    if (mins != null) setForm((f) => ({ ...f, sla_first_response_minutes: mins }));
  };
  const onFirstUnitChange = (unit) => {
    setForm((f) => ({ ...f, sla_first_response_unit: unit }));
    setFirstDisplay(String(minutesToUnit(form.sla_first_response_minutes, unit, hpd)));
  };
  const onCloseValueChange = (v) => {
    setCloseDisplay(v);
    const hrs = unitToHours(v, form.sla_time_to_close_unit, hpd);
    if (hrs != null) setForm((f) => ({ ...f, sla_time_to_close_hours: hrs }));
  };
  const onCloseUnitChange = (unit) => {
    setForm((f) => ({ ...f, sla_time_to_close_unit: unit }));
    setCloseDisplay(String(hoursToUnit(form.sla_time_to_close_hours, unit, hpd)));
  };

  // When the business-hours window changes, re-derive the display values so
  // "Days" stays accurate against the new business-day length.
  useEffect(() => {
    setFirstDisplay(String(minutesToUnit(form.sla_first_response_minutes, form.sla_first_response_unit, hpd)));
    setCloseDisplay(String(hoursToUnit(form.sla_time_to_close_hours, form.sla_time_to_close_unit, hpd)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hpd]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/settings", form);
      toast.success("SLA settings saved");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const recompute = async () => {
    setRecomputing(true);
    try {
      const { data } = await api.post("/sla/recompute");
      toast.success(`Recomputed SLA on ${data.updated} leads`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recompute failed");
    } finally {
      setRecomputing(false);
    }
  };

  const toggleDay = (v) => {
    const cur = form.sla_business_days;
    const next = cur.includes(v) ? cur.filter((d) => d !== v) : [...cur, v].sort();
    setForm({ ...form, sla_business_days: next });
  };

  // Override editor — value/unit pairs per key.
  const setOverrideValue = (kind, key, kindField, displayValue, unit) => {
    const ovs = { ...(form[kind] || {}) };
    const row = { ...(ovs[key] || {}) };
    // canonical conversion (business-hours-aware)
    if (displayValue === "" || displayValue == null) {
      delete row[kindField];
    } else if (kindField === "first_response_minutes") {
      const mins = unitToMinutes(displayValue, unit, hpd);
      if (mins != null) row[kindField] = mins;
    } else if (kindField === "time_to_close_hours") {
      const hrs = unitToHours(displayValue, unit, hpd);
      if (hrs != null) row[kindField] = hrs;
    }
    // persist unit alongside (used by the UI on re-render)
    const unitField = kindField === "first_response_minutes" ? "first_response_unit" : "time_to_close_unit";
    row[unitField] = unit;
    // drop the row entirely if no canonical values remain
    const keepKeys = Object.keys(row).filter((k) => !k.endsWith("_unit"));
    if (keepKeys.length === 0) {
      delete ovs[key];
    } else {
      ovs[key] = row;
    }
    setForm({ ...form, [kind]: ovs });
  };

  return (
    <div data-testid="sla-tab" className="space-y-8 max-w-4xl">
      <section className="bg-white border border-slate-200 rounded-sm p-6">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="h-12 w-12 rounded-sm bg-amber-50 flex items-center justify-center shrink-0">
            <Timer size={24} className="text-amber-600" weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-2xl text-slate-900">Service Level Agreement</h2>
            <p className="text-sm text-slate-500 mt-1">
              Targets for how quickly leads should be actioned. We track two stages — <em>first response</em>
              {" "}(status leaves <b>New</b>) and <em>time to close</em> (status moves to <b>Won</b> or <b>Lost</b>).
              Pick the unit that suits each target — minutes, hours, or days.
            </p>
          </div>
          <Button
            data-testid="sla-recompute-btn"
            variant="outline"
            size="sm"
            className="rounded-sm gap-2"
            onClick={recompute}
            disabled={recomputing}
          >
            <ArrowsClockwise size={12} /> {recomputing ? "Recomputing…" : "Recompute all"}
          </Button>
        </div>
      </section>

      {/* Global targets */}
      <section className="bg-white border border-slate-200 rounded-sm p-6 space-y-5">
        <div className="label-tracking">Global targets</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="label-tracking">First response</Label>
            <NumberWithUnit
              testid="sla-first-response"
              value={firstDisplay}
              unit={form.sla_first_response_unit}
              onChange={onFirstValueChange}
              onUnitChange={onFirstUnitChange}
            />
            <p className="text-[11px] text-slate-400 mt-1">From lead arrival until status leaves New.</p>
          </div>
          <div>
            <Label className="label-tracking">Time to close</Label>
            <NumberWithUnit
              testid="sla-time-to-close"
              value={closeDisplay}
              unit={form.sla_time_to_close_unit}
              onChange={onCloseValueChange}
              onUnitChange={onCloseUnitChange}
            />
            <p className="text-[11px] text-slate-400 mt-1">From lead arrival until status is Won or Lost.</p>
          </div>
        </div>
      </section>

      {/* Business hours */}
      <section className="bg-white border border-slate-200 rounded-sm p-6 space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="label-tracking">Business hours</div>
          <div className="flex items-center gap-3">
            <Label htmlFor="sla_bh_only" className="text-xs text-slate-600">Only count business hours</Label>
            <Switch
              id="sla_bh_only"
              data-testid="sla-business-hours-switch"
              checked={form.sla_business_hours_only}
              onCheckedChange={(v) => setForm({ ...form, sla_business_hours_only: v })}
            />
          </div>
        </div>
        <div className={`grid grid-cols-2 gap-4 ${form.sla_business_hours_only ? "" : "opacity-50 pointer-events-none"}`}>
          <div>
            <Label className="label-tracking">Start (24h)</Label>
            <Input
              data-testid="sla-start-input"
              type="time"
              value={form.sla_business_start}
              onChange={(e) => setForm({ ...form, sla_business_start: e.target.value })}
              className="mt-2 h-10 rounded-sm font-mono"
            />
          </div>
          <div>
            <Label className="label-tracking">End (24h)</Label>
            <Input
              data-testid="sla-end-input"
              type="time"
              value={form.sla_business_end}
              onChange={(e) => setForm({ ...form, sla_business_end: e.target.value })}
              className="mt-2 h-10 rounded-sm font-mono"
            />
          </div>
        </div>
        <div className={form.sla_business_hours_only ? "" : "opacity-50 pointer-events-none"}>
          <Label className="label-tracking">Days</Label>
          <div className="mt-2 flex flex-wrap gap-2">
            {DAYS.map((d) => {
              const on = form.sla_business_days.includes(d.val);
              return (
                <button
                  type="button"
                  key={d.val}
                  data-testid={`sla-day-${d.label}`}
                  onClick={() => toggleDay(d.val)}
                  className={`px-3 py-1.5 rounded-sm text-xs font-medium transition-colors ${on ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
                >
                  {d.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Notifications */}
      <section className="bg-white border border-slate-200 rounded-sm p-6 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-tracking">Breach notifications</div>
            <p className="text-[11px] text-slate-500 mt-1">
              When enabled, the system fires the <span className="font-mono">sla_breach_first_response</span> and
              {" "}<span className="font-mono">sla_breach_close</span> email templates.
              Edit them in the Email Routing tab.
            </p>
          </div>
          <Switch
            data-testid="sla-notify-switch"
            checked={form.sla_breach_notify_enabled}
            onCheckedChange={(v) => setForm({ ...form, sla_breach_notify_enabled: v })}
          />
        </div>
      </section>

      {/* Department overrides */}
      <section className="bg-white border border-slate-200 rounded-sm p-6 space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <Buildings size={14} className="text-indigo-600" />
          <div className="label-tracking">Department overrides</div>
        </div>
        <p className="text-[11px] text-slate-500 mb-3">
          Set tighter targets per department. Leave blank to inherit the global default.
        </p>
        <OverrideTable
          kind="sla_department_overrides"
          keys={departments}
          values={form.sla_department_overrides}
          defaultFirstUnit={form.sla_first_response_unit}
          defaultCloseUnit={form.sla_time_to_close_unit}
          hoursPerDay={hpd}
          onChange={(k, f, v, unit) => setOverrideValue("sla_department_overrides", k, f, v, unit)}
          dataTestid="dept"
        />
      </section>

      {/* Source overrides */}
      <section className="bg-white border border-slate-200 rounded-sm p-6 space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <Funnel size={14} className="text-emerald-600" />
          <div className="label-tracking">Source overrides</div>
        </div>
        <p className="text-[11px] text-slate-500 mb-3">
          Set tighter targets per lead source (e.g. urgent "Walk-in" enquiries).
        </p>
        <OverrideTable
          kind="sla_source_overrides"
          keys={["Website Contact Form", "SEESA Referral", "Email", "Walk-in", "Phone", "Other"]}
          values={form.sla_source_overrides}
          defaultFirstUnit={form.sla_first_response_unit}
          defaultCloseUnit={form.sla_time_to_close_unit}
          hoursPerDay={hpd}
          onChange={(k, f, v, unit) => setOverrideValue("sla_source_overrides", k, f, v, unit)}
          dataTestid="source"
        />
      </section>

      <div className="flex items-center gap-3 sticky bottom-4 z-10">
        <Button
          data-testid="sla-save-btn"
          onClick={save}
          disabled={saving}
          className="rounded-sm bg-slate-900 hover:bg-slate-800 gap-2"
        >
          <FloppyDisk size={14} /> {saving ? "Saving…" : "Save SLA settings"}
        </Button>
        <span className="text-[11px] text-slate-500 inline-flex items-center gap-1">
          <Lightning size={11} /> Changes apply to new leads instantly. Use "Recompute all" to refresh existing ones.
        </span>
      </div>
    </div>
  );
}

function OverrideTable({ keys, values, onChange, dataTestid, defaultFirstUnit, defaultCloseUnit, hoursPerDay = 24 }) {
  if (keys.length === 0) {
    return <p className="text-xs text-slate-400">No options yet.</p>;
  }
  return (
    <div className="border border-slate-100 rounded-sm overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500">
          <tr>
            <th className="text-left p-3 label-tracking !text-[0.6rem]">Key</th>
            <th className="text-left p-3 label-tracking !text-[0.6rem]">First response</th>
            <th className="text-left p-3 label-tracking !text-[0.6rem]">Time to close</th>
          </tr>
        </thead>
        <tbody>
          {keys.map((k) => {
            const row = values[k] || {};
            const firstUnit = row.first_response_unit || defaultFirstUnit || "hours";
            const closeUnit = row.time_to_close_unit || defaultCloseUnit || "days";
            const firstVal = row.first_response_minutes != null
              ? String(minutesToUnit(row.first_response_minutes, firstUnit, hoursPerDay))
              : "";
            const closeVal = row.time_to_close_hours != null
              ? String(hoursToUnit(row.time_to_close_hours, closeUnit, hoursPerDay))
              : "";
            return (
              <tr key={k} className="border-t border-slate-100">
                <td className="p-3 text-slate-700 text-xs align-middle">{k}</td>
                <td className="p-3">
                  <OverrideCell
                    value={firstVal}
                    unit={firstUnit}
                    onValueChange={(v) => onChange(k, "first_response_minutes", v, firstUnit)}
                    onUnitChange={(u) => onChange(k, "first_response_minutes", firstVal, u)}
                    testid={`override-${dataTestid}-${k}-first`}
                  />
                </td>
                <td className="p-3">
                  <OverrideCell
                    value={closeVal}
                    unit={closeUnit}
                    onValueChange={(v) => onChange(k, "time_to_close_hours", v, closeUnit)}
                    onUnitChange={(u) => onChange(k, "time_to_close_hours", closeVal, u)}
                    testid={`override-${dataTestid}-${k}-close`}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function OverrideCell({ value, unit, onValueChange, onUnitChange, testid }) {
  return (
    <div className="flex items-center gap-1.5">
      <Input
        data-testid={testid}
        type="number"
        min="0"
        step="any"
        value={value}
        placeholder="—"
        onChange={(e) => onValueChange(e.target.value)}
        className="h-8 rounded-sm w-20 text-xs"
      />
      <Select value={unit} onValueChange={onUnitChange}>
        <SelectTrigger
          data-testid={`${testid}-unit`}
          className="h-8 w-24 rounded-sm text-xs"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {UNIT_OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
